import type { MapData, WorldEntity } from '../../engine/world/types';
import { generateWorld } from '../worldgen/WorldGenerator';
import type { RoadGraph } from '../worldgen/types';

interface Point {
  x: number;
  y: number;
}

export interface MapLayout {
  readonly map: MapData;
  readonly zones: Record<string, Point>;
  readonly buildings: readonly WorldEntity[];
  readonly decorations: readonly WorldEntity[];
  readonly roadGraph: RoadGraph;
  readonly diagnostics: readonly string[];
}

interface MapLayoutOptions {
  readonly kingdomId?: string;
  readonly visualPolish?: boolean;
  readonly waterMode?: 'tiles';
  readonly growthLevel?: number;
}

const width = 34;
const height = 20;

export function createMapLayout(options: MapLayoutOptions = {}): MapLayout {
  const world = generateWorld({
    kingdomId: options.kingdomId,
    width,
    height,
    visualPolish: Boolean(options.visualPolish),
    waterMode: options.waterMode,
    targetPhaseIndex: options.growthLevel,
  });

  return {
    map: world.map,
    zones: Object.fromEntries(world.settlementGraph.nodes.map((node) => [node.id, { x: node.x, y: node.y }])),
    buildings: world.buildings,
    decorations: world.decorations,
    roadGraph: world.roadGraph,
    diagnostics: flattenDiagnostics(world.diagnostics),
  };
}

function flattenDiagnostics(diagnostics: {
  readonly missingTiles: readonly string[];
  readonly invalidRoads: readonly string[];
  readonly invalidWater: readonly string[];
  readonly blockedPlots: readonly string[];
  readonly performanceWarnings: readonly string[];
  readonly notes: readonly string[];
}): readonly string[] {
  return [
    ...diagnostics.notes,
    ...diagnostics.missingTiles.map((message) => `Fehlendes Tile: ${message}`),
    ...diagnostics.invalidRoads.map((message) => `Straßenfehler: ${message}`),
    ...diagnostics.invalidWater.map((message) => `Wasserfehler: ${message}`),
    ...diagnostics.blockedPlots.map((message) => `Blockiertes Grundstück: ${message}`),
    ...diagnostics.performanceWarnings.map((message) => `Performance: ${message}`),
  ];
}
